import { getNinjaConfig } from '@/config/ninja';
import { GENERAL_EVENTS } from '@/const/events';
import { cleanCookie } from '@/cookies/cleanCookie';
import { cookieStorage } from '@/cookies/cookieStorage';
import { getCookieExpirationDate } from '@/cookies/getCookieExpirationDate';
import { getCookieName } from '@/cookies/getCookieName';
import { ninjaBus } from '@/eventbus';
import { getLogger } from '@/logger';
import { Event } from '@/types/events';
import { loadScript } from '@/utils/browser';
import { setDefaultGoogleConsents } from '@/utils/consent';
import { makeMapping } from '@/utils/mapping';
import { normalizeEventValue } from '@/utils/string';
import { BaseTracker } from './BaseTracker';

const logger = getLogger('GtmTracker');

export class GtmTracker extends BaseTracker {
  cookies: string[];
  scriptTagId = 'gtm-script-tag';
  scriptUrl = 'https://www.googletagmanager.com/gtm.js?id=';

  constructor() {
    super();

    this.trackerName = 'gtm';
    this.cookies = ['invite', 'ldTd'];
    this.listeners = this.createStandardTrackingListeners();
  }

  async init() {
    if (this.isDisabled()) {
      this.state = 'disabled';
      return this;
    }

    const oldTag = document.getElementById(this.scriptTagId);
    if (oldTag) {
      logger.debug(`${this.trackerName} already exists on the page`);
      return this;
    }

    try {
      (window as any)[`ga-disable-${this.getToken()}`] = false;

      this.state = 'loading';
      setDefaultGoogleConsents('dataLayer');

      window.dataLayer.push({
        processed: true,
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      });

      const url = this.scriptUrl + this.getToken();
      await loadScript({
        url,
        id: this.scriptTagId,
        timeout: 2000,
      });
      logger.debug('GTM tracker initialized');
      this.state = 'ready';
    } catch (err) {
      logger.error('GTM tracker initialization error', err);
      this.state = 'error';
      throw err;
    }

    return this;
  }

  isDisabled() {
    return super.isDisabled() || !!getNinjaConfig().custom?.gtm?.disable;
  }

  async trackEvent(e: Event) {
    if (!this.shouldTrackEvent(e)) return;

    const eventName = encodeURIComponent(normalizeEventValue(e.value, 'dash') as string);
    const mappedEvent = makeMapping(this.trackerName, 'trackEvent', eventName);
    if (!mappedEvent) {
      logger.debug('Values not found in the mapping. Event tracking aborted', e.value);
      return;
    }

    try {
      window.dataLayer.push({
        processed: true,
        event: mappedEvent.value,
      });
      logger.debug('GTM event tracked', e.actionType, eventName, e.props);
    } catch (error) {
      logger.error('Failed to track GTM event', error, e.actionType, eventName, e.props);
    }
  }

  async trackPage(e: Event) {
    if (!this.shouldTrackEvent(e)) return;

    const mappedPage = makeMapping(this.trackerName, 'trackPage', e.value);
    if (!mappedPage) {
      logger.debug('Values not found in the mapping. Page tracking aborted', e.value);
      return;
    }

    try {
      window.dataLayer.push({
        processed: true,
        event: mappedPage.value,
      });
      logger.debug('GTM page tracked', e.actionType, e.value, e.props);
    } catch (error) {
      logger.error('Failed to track GTM page', error, e.actionType, e.value, e.props);
    }
  }

  // GTM can't be unloaded from the page
  cleanup() {
    super.cleanup();
    this.cookies.forEach(cookie => cleanCookie(cookie));
    (window as any)[`ga-disable-${this.getToken()}`] = true;
  }

  async getCustomParams(e: Event, useCustomDataLayer: boolean) {
    const trackParams: Record<string, unknown> = useCustomDataLayer ? this.buildDataLayerParams(e) : {};

    const invite = await this.getInviteParamsFromEventOrCookie(e);
    if (invite) {
      trackParams.invs = invite.invs.toLowerCase();
      trackParams.invc = invite.invc.toLowerCase();
      ninjaBus.emit(GENERAL_EVENTS.TRACK_FEATURE_USAGE, '', { message: 'GtmTracker/invite params available' });
    }

    const landing = this.getLandingPageParam(e);
    if (landing) {
      trackParams.landing_page = landing;
    }

    if (Object.keys(trackParams).length === 0) {
      return null;
    }
    return trackParams;
  }

  private buildDataLayerParams(e: Event): Record<string, unknown> {
    const params = this.getTrackParams(e);
    if (e.actionType === 'trackPage' && e.value) {
      params.trackPage = e.value;
    }
    return params;
  }

  private async getInviteParamsFromEventOrCookie(e: Event): Promise<{ invs: string; invc: string } | null> {
    const cookieNameIv = getCookieName('invite');
    const eventMatch = e.meta?.invite?.match(/^\s*([a-zA-Z0-9-]{1,64})(?:_([a-zA-Z0-9%-]{0,128})).*/);

    try {
      if (eventMatch) {
        const value = `"sr=${eventMatch[1]}&cn=${eventMatch[2]}&td=${Math.floor(Date.now() / 1000)}"`;
        cookieStorage.set(cookieNameIv, value, {
          expires: 30,
          path: '/',
          domain: getNinjaConfig().cookieDomain,
        });
        return { invs: eventMatch[1], invc: eventMatch[2] };
      }

      const storedMatch = (cookieStorage.get(cookieNameIv) || '').match(/"sr=([A-Za-z0-9-]+)&cn=([A-Za-z0-9%-]+)&td=[0-9]+"/);
      if (storedMatch) {
        return { invs: storedMatch[1], invc: storedMatch[2] };
      }
    } catch (error) {
      logger.error('Failed to handle invite', error, e);
    }
    return null;
  }

  private getLandingPageParam(e: Event): string | null {
    const cookieNameLd = getCookieName('ldTd');
    try {
      const hasLandingCookie = (cookieStorage.get(cookieNameLd) || '').match(/true/);
      if (hasLandingCookie !== null) {
        return null;
      }
      cookieStorage.set(cookieNameLd, 'true', {
        expires: getCookieExpirationDate(1, 'min'),
        path: '/',
        domain: getNinjaConfig().cookieDomain,
      });
      return 'true';
    } catch (error) {
      logger.error('Failed to handle landing page', error, e);
      return null;
    }
  }

  async handleInviteParams(e: Event) {
    const invite = await this.getInviteParamsFromEventOrCookie(e);
    if (invite) {
      return [null, invite.invs, invite.invc];
    }
    return [];
  }
}
